import ReactECharts from 'echarts-for-react';
import { useState } from 'react';
import { PlayerGoalFaceData, PlayerInfo, GoalFaceEntry } from 'types/models/OrgSnapshotData';
import { t } from 'utilities/TranslationUtils';
import {
  darkTooltip, bottomLegend,
  kickResultScatterSeries, formatDateTimeTooltip, formatKickIdTooltip,
} from 'utilities/chartUtils';

// Goal dimensions (metres)
const GOAL_WIDTH = 7.32;     // ±3.66m from centre
const GOAL_HEIGHT = 2.44;
const MARGIN_SIDE = 1.5;     // extra metres visible beyond posts
const MARGIN_TOP = 1.0;
// Tighter framing for the detail strip: the goal fills more of the width.
const FILL_MARGIN_SIDE = 0.9;
const FILL_MARGIN_TOP = 0.5;
// Box shape of the fill-mode chart: plot area (9.12 m × 3.24 m of goal + margins, ≈2.8:1) plus the fixed
// pixel padding around it. The box is the largest of this shape that fits the parent (container-query units), so the
// goal always keeps its real proportions — the parent must be a size container (`container-type: size`).
const FILL_ASPECT = 2.33;
const UNSELECTED_OPACITY = 0.4;

type Props = {
  data: PlayerGoalFaceData[];
  players: PlayerInfo[];
  colors: string[];
  sessionTypeFilter?: string[] | null;
  /** When set, the tooltip for this kick omits the "open measurement details" link (we're already there). */
  currentMeasurementId?: string | null;
  /** Draw the goal frame even when there are no kicks (single-kick detail view) instead of a "no data" state. */
  alwaysShowFrame?: boolean;
  /** Size the chart by the parent's height (centred, goal keeps its real proportions) with tighter margins, instead of the 12:5 box. */
  fill?: boolean;
  /** Detail view: make markers selectable — tapping one calls this. Also dims every kick except `currentMeasurementId` (hover restores full opacity). */
  onSelectKick?: (kickId: string, sessionId: string) => void;
};

// GoalFaceEntry augmented with player identity for filtering and tooltip display.
type TaggedEntry = GoalFaceEntry & { playerName: string; playerId: string };

type ScatterTuple = [number, number, string, string, string, number | null, number | null, string, string];

/** ECharts hands back either the raw tuple or a `{ value: tuple }` wrapper (detail view) — normalise to the tuple. */
const tupleOf = (data: unknown): ScatterTuple =>
  (Array.isArray(data) ? data : (data as { value: ScatterTuple }).value) as ScatterTuple;

const GoalFrontViewChart = ({ data, players, colors, sessionTypeFilter, currentMeasurementId = null, alwaysShowFrame = false, fill = false, onSelectKick }: Props) => {
  const marginSide = fill ? FILL_MARGIN_SIDE : MARGIN_SIDE;
  const marginTop = fill ? FILL_MARGIN_TOP : MARGIN_TOP;
  const [hiddenPlayerIds, setHiddenPlayerIds] = useState<Set<string>>(new Set());

  const togglePlayer = (id: string) =>
    setHiddenPlayerIds((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  // Combine all players' entries, tagging each with player identity for filtering and tooltip.
  let entries: TaggedEntry[] = data.flatMap((pd) => {
    const playerName = players.find((p) => p.playerId === pd.playerId)?.playerName ?? '';
    return (pd.entries ?? []).map((e) => ({ ...e, playerName, playerId: pd.playerId }));
  });
  if (sessionTypeFilter) {
    entries = entries.filter((e) => sessionTypeFilter.includes(e.sessionType));
  }
  if (hiddenPlayerIds.size > 0) {
    entries = entries.filter((e) => !hiddenPlayerIds.has(e.playerId));
  }

  // Multi-kick views (Players / Player Performance) show a "no data" state when empty. The single-kick
  // detail view instead always draws the goal frame (alwaysShowFrame), so it never blanks between kicks.
  if (!alwaysShowFrame && data.every((d) => d.entries.length === 0)) {
    return <p className='chart-empty'>{t('players.noData')}</p>;
  }

  // Build scatter series coloured by kick result.
  // Data tuple: [z, y, kickId, sessionId, dateTime, ballSpeed, hangTime, playerName, kickResult]
  // Drop result series with no visible kicks so the legend only lists results actually present.
  const series: any[] = kickResultScatterSeries(
    entries,
    (e) => [e.z, e.y, e.kickId, e.sessionId, e.dateTime, e.ballSpeed ?? null, e.hangTime ?? null, e.playerName, e.kickResult],
  ).filter((s) => Array.isArray(s.data) && s.data.length > 0);

  // Detail view: selected kick at full opacity, the player's other kicks dimmed until hovered.
  if (onSelectKick) {
    for (const sr of series) {
      sr.data = (sr.data as unknown[][]).map((tuple) => ({
        value: tuple,
        itemStyle: { opacity: tuple[2] === currentMeasurementId ? 1 : UNSELECTED_OPACITY },
      }));
      sr.cursor = 'pointer';
      sr.emphasis = { focus: 'none', scale: 1.25, itemStyle: { opacity: 1 } };
    }
  }

  // Goal frame rendered as markLine
  const halfW = GOAL_WIDTH / 2;
  const goalFrameLines = [
    [{ coord: [-halfW, 0] }, { coord: [-halfW, GOAL_HEIGHT] }],
    [{ coord: [halfW, 0] }, { coord: [halfW, GOAL_HEIGHT] }],
    [{ coord: [-halfW, GOAL_HEIGHT] }, { coord: [halfW, GOAL_HEIGHT] }],
    [{ coord: [-(halfW + marginSide), 0] }, { coord: [halfW + marginSide, 0] }],
  ];

  // Always draw the goal frame + pitch line — even with no kicks — so the goal is shown regardless.
  // Attach it to the first real series when present, otherwise a dedicated invisible carrier series.
  const frameHost = series.length > 0
    ? series[0]
    : (() => { const s: any = { type: 'scatter', data: [], silent: true, legendHoverLink: false }; series.push(s); return s; })();
  frameHost.markLine = {
    silent: true,
    symbol: 'none',
    lineStyle: { color: '#374151', width: 3, type: 'solid' },
    data: goalFrameLines,
    label: { show: false },
    animation: false,
  };
  frameHost.markArea = {
    silent: true,
    data: [[{ coord: [-halfW, 0] }, { coord: [halfW, GOAL_HEIGHT] }]],
    itemStyle: { color: 'rgba(229,231,235,0.25)', borderWidth: 0 },
  };

  const option = {
    tooltip: {
      ...darkTooltip('item'),
      formatter: (params: any) => {
        const [z, y, kickId, sessionId, dateTime, ballSpeed, hangTime, playerName, kickResult] = tupleOf(params.data);
        // Show the specific result (e.g. "Saved") to match the header chip, not the bucket series name.
        const resultLabel = kickResult ? t(`sessions.kickResult_${kickResult}`) : params.seriesName;
        return [
          `<b>${playerName}</b> — ${resultLabel}`,
          `Lateral: ${z > 0 ? 'Right' : 'Left'} ${Math.abs(z).toFixed(2)} m`,
          `Height: ${y.toFixed(2)} m`,
          ballSpeed != null ? `Ball Speed: ${ballSpeed} km/h` : '',
          hangTime != null ? `Hang Time: ${hangTime}s` : '',
          dateTime ? formatDateTimeTooltip(dateTime) : '',
          formatKickIdTooltip(kickId, sessionId, kickId !== currentMeasurementId),
        ].filter(Boolean).join('<br/>');
      },
    },
    legend: bottomLegend(),
    grid: { left: 50, right: 30, top: 20, bottom: 50, containLabel: false },
    xAxis: {
      type: 'value',
      min: -(halfW + marginSide),
      max: halfW + marginSide,
      name: 'm',
      nameLocation: 'middle' as const,
      nameGap: 24,
      nameTextStyle: { fontSize: 11, color: '#D1D5DB' },
      splitLine: { lineStyle: { color: '#F3F4F6' } },
      axisLabel: { fontSize: 10, color: '#D1D5DB', formatter: (v: number) => `${v.toFixed(1)}` },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: -0.3,
      max: GOAL_HEIGHT + marginTop,
      name: 'm',
      nameTextStyle: { fontSize: 11, color: '#D1D5DB' },
      splitLine: { lineStyle: { color: '#F9FAFB' } },
      axisLabel: { fontSize: 10, color: '#D1D5DB', formatter: (v: number) => `${v.toFixed(1)}` },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series,
  };

  return (
    <div style={fill ? { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' } : undefined}>
      {players.length > 1 && (
        <div className='chart-player-switcher'>
          {players.map((p, i) => {
            const active = !hiddenPlayerIds.has(p.playerId);
            return (
              <button
                key={p.playerId}
                type='button'
                className={`player-switch-btn${active ? ' player-switch-btn--active' : ''}`}
                style={{ '--switch-color': colors[i] } as React.CSSProperties}
                onClick={() => togglePlayer(p.playerId)}
              >
                <span className='player-switch-dot' />
                {p.playerName.split(' ')[0]}
              </button>
            );
          })}
        </div>
      )}
      {/* Constrain to a near-real-world aspect (data is ~10.3 m wide × ~3.7 m tall incl. margins) and
          centre it, so the goal keeps its true 7.32 × 2.44 proportions instead of stretching to full width. */}
      <div style={fill ? { width: `min(100cqw, calc(100cqh * ${FILL_ASPECT}))`, aspectRatio: String(FILL_ASPECT) } : { width: '100%', maxWidth: 820, aspectRatio: '12 / 5', margin: '0 auto' }}>
        <ReactECharts
          option={option}
          style={{ width: '100%', height: '100%' }}
          notMerge
          onEvents={onSelectKick ? {
            click: (params: { seriesType?: string; data?: unknown }) => {
              if (params.seriesType !== 'scatter' || !params.data) return;
              const [, , kickId, sessionId] = tupleOf(params.data);
              if (kickId && kickId !== currentMeasurementId) onSelectKick(kickId, sessionId);
            },
          } : undefined}
        />
      </div>
    </div>
  );
};

export default GoalFrontViewChart;
